/**
 * PawLinkQR — canonical PawLink pet QR (v7.93 spec — 2026-02-25).
 *
 * SPEC (locked in — approved Option D "isolated dots"):
 *   • Standard ISO/IEC 18004 square QR, Error Correction Level H
 *   • 4-module white quiet zone
 *   • Data modules rendered as TRUE ISOLATED CIRCULAR DOTS in BLACK
 *     (#111111) — no maze-like connections between neighbours.
 *   • Extra-rounded corner-finder outers in BLACK + orange inner square
 *     eye (#F87A2A) — the signature PawLink look
 *   • Center overlay = the TRANSPARENT PawLink brand mark from
 *     /brand/icon-transparent-512.png — black "P" outline (recoloured
 *     from white for QR-white-background contrast), orange dog head,
 *     fully transparent background. No box, no circle, no badge.
 *   • imageSize 0.22 → ~5% area, well inside Level H's ~30% envelope.
 *
 * Callers do NOT need to pass `variant="D"` — Option D is the default
 * across every entry point (component + renderBrandedQrCanvas +
 * downloadBrandedQR + downloadBrandedSvg). The `variant` prop is
 * retained for the "A" comparison harness only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";

export const PAW_ORANGE = "#F87A2A";
export const PAW_BLACK = "#111111";
// Canonical PawLink brand icon — the TRANSPARENT variant already shipped
// and used by the website header (`PawLinkLogo.jsx`). No black rounded
// square, no boxed background. On a white QR the white "P" outline blends
// into the background and only the orange dog-head silhouette remains
// visible — a subtle, premium brand accent that complements (rather than
// competes with) the QR's finder corners.
const PAW_LOGO_IMG = "/brand/icon-transparent-512.png";

// ---------- Center composite builder ----------------------------------
// The composite reuses the existing transparent PawLink brand mark and
// programmatically RECOLOURS its white portion to PAW_BLACK — without
// touching geometry, proportions, or the orange dog-head. This gives us
// a fully visible dark-mark-on-transparent-background version of the
// official logo, matching v7.92 spec:
//   • dark "P" / outer mark      → #111111 (PAW_BLACK)
//   • dog-head silhouette        → PawLink orange (unchanged)
//   • background                 → fully transparent
//   • no box, circle, badge, or text
async function buildCenterCompositePng(/* opts intentionally ignored */) {
    const W = 1200;
    const H = 1200;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, W, H);

    let logo = null;
    try { logo = await loadImage(PAW_LOGO_IMG); } catch { /* proceed without */ }
    if (!logo) return canvas.toDataURL("image/png");

    // Draw the source asset once, then rewrite pixels: any near-white
    // pixel becomes PAW_BLACK; orange / dark / transparent pixels are
    // preserved untouched. No geometry change — just a colour swap that
    // makes the outer "P" mark visible on a white QR background.
    ctx.drawImage(logo, 0, 0, W, H);
    try {
        const img = ctx.getImageData(0, 0, W, H);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
            const a = d[i + 3];
            if (a === 0) continue;                  // transparent — leave
            const r = d[i], g = d[i + 1], b = d[i + 2];
            // near-white (the P outline + inner white glyph) → PAW_BLACK
            if (r > 200 && g > 200 && b > 200) {
                d[i] = 0x11; d[i + 1] = 0x11; d[i + 2] = 0x11;
            }
            // dark / grey shadow pixels around the outline → also PAW_BLACK
            // so the mark reads as a clean solid dark stroke, not a soft edge.
            else if (r < 80 && g < 80 && b < 80) {
                d[i] = 0x11; d[i + 1] = 0x11; d[i + 2] = 0x11;
            }
            // everything else (the orange dog-head + antialiased edges)
            // stays exactly as designed.
        }
        ctx.putImageData(img, 0, 0);
    } catch {
        // getImageData can throw if the source image is cross-origin
        // tainted. In that case we fall back to the untinted draw above —
        // the QR still scans, just without the dark-P upgrade.
    }

    return canvas.toDataURL("image/png");
}

// Cross-origin-safe image loader. Falls back to same-origin if CORS is denied.
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => {
            // Retry without crossOrigin (some CDNs strip CORS headers)
            const retry = new Image();
            retry.onload = () => resolve(retry);
            retry.onerror = reject;
            retry.src = src;
        };
        img.src = src;
    });
}

// ---------- QR builder ---------------------------------------------------
// Build a qr-code-styling instance with the official PawLink look.
//
// Variants (`variant`):
//   "A" — v7.90 default: rounded modules (soft blob look; slight
//         connection between adjacent modules in dense areas).
//   "D" — v7.93 candidate: true circular ISOLATED dots (`type: "dots"`),
//         zero connection between neighbours, maximum white breathing
//         space, premium minimal look for print-ready pet tags.
//
// Both variants share:
//   • Level H error correction, 4-module quiet zone
//   • Extra-rounded BLACK corner-finder outers
//   • ORANGE inner-eye squares (PawLink signature)
//   • The v7.92 transparent center composite (black P outline + orange
//     dog head, no box), at imageSize 0.22 (~5% area, well inside
//     Level H's ~30% recovery envelope)
//   • Same QR data — no payload change
export function buildPawLinkQR(value, size, centerImageDataUrl, variant = "D") {
    const dotsType = variant === "D" ? "dots" : "rounded";
    return new QRCodeStyling({
        width: size,
        height: size,
        type: "svg",
        data: value,
        margin: 4,
        qrOptions: { errorCorrectionLevel: "H" },
        dotsOptions:          { type: dotsType,        color: PAW_BLACK },
        cornersSquareOptions: { type: "extra-rounded", color: PAW_BLACK },
        cornersDotOptions:    { type: "square",        color: PAW_ORANGE },
        backgroundOptions:    { color: "#FFFFFF" },
        image: centerImageDataUrl || PAW_LOGO_IMG,
        imageOptions: {
            hideBackgroundDots: true,
            imageSize: 0.22,
            margin: 0,
            crossOrigin: "anonymous",
        },
    });
}

export default function PawLinkQR({
    value,
    size = 288,
    // eslint-disable-next-line no-unused-vars
    avatarUrl,  // Deprecated (v7.86): no longer used. Kept in signature
                // so existing callers don't need to change.
    onReady,
    id,
    className = "",
    variant = "D",
    "data-testid": testId = "pawlink-qr",
}) {
    const hostRef = useRef(null);
    const [centerImg, setCenterImg] = useState(null);
    const [ready, setReady] = useState(false);

    // Composite is now static (no avatar). Build once on mount.
    useEffect(() => {
        let cancelled = false;
        buildCenterCompositePng()
            .then((dataUrl) => { if (!cancelled) setCenterImg(dataUrl); })
            .catch(() => { if (!cancelled) setCenterImg(null); });
        return () => { cancelled = true; };
    }, []);

    const qr = useMemo(
        () => (value && centerImg) ? buildPawLinkQR(value, size, centerImg, variant) : null,
        [value, size, centerImg, variant]
    );

    useEffect(() => {
        if (!hostRef.current || !qr) return;
        hostRef.current.innerHTML = "";
        qr.append(hostRef.current);
        setReady(true);
        if (typeof onReady === "function") onReady(qr);
    }, [qr, onReady]);

    return (
        <div
            id={id}
            data-testid={testId}
            ref={hostRef}
            className={`inline-block bg-white ${className}`}
            style={{ width: size, height: size }}
        />
    );
}

// ---- Export helpers ------------------------------------------------------
/**
 * Get a print-quality PNG data URL for the QR (with the branded centre baked
 * in). pixelSize defaults to 1600 px → ~300 DPI on a 5.3" print. `opts` is
 * kept for backward-compat but no options are currently read (v7.86 stripped
 * avatar support from the center).
 */
export async function renderBrandedQrCanvas(url, pixelSize = 1600, opts = {}) {
    const { variant = "D" } = opts;
    const centerImg = await buildCenterCompositePng();
    const qr = buildPawLinkQR(url, pixelSize, centerImg, variant);
    const blob = await qr.getRawData("png");
    const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(blob);
    });
    return { dataUrl, width: pixelSize, height: pixelSize, aspectRatio: 1 };
}

export async function downloadBrandedQR(url, filename, options = {}) {
    const { pixelSize = 1600, variant = "D" } = options;
    const { dataUrl } = await renderBrandedQrCanvas(url, pixelSize, { variant });
    trigger(dataUrl, `${filename}.png`);
}

export async function downloadBrandedSvg(url, filename, options = {}) {
    const { pixelSize = 1600, variant = "D" } = options;
    const centerImg = await buildCenterCompositePng();
    const qr = buildPawLinkQR(url, pixelSize, centerImg, variant);
    const blob = await qr.getRawData("svg");
    const href = URL.createObjectURL(blob);
    trigger(href, `${filename}.svg`, true);
}

function trigger(href, filename, revoke = false) {
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    if (revoke) setTimeout(() => URL.revokeObjectURL(href), 1000);
}
